import * as React from 'react';
import { formatValue} from '../../../utils/common';
import { Dictionary } from '../../../../entities/common';
import { ViewType } from '../ResourceUsageGrid';
import { CellSummaryData, HoursData, TimeType } from './common';
import CellValue from './CellValue';

type ViewModeProps = {
    viewType: ViewType,
    timeType: TimeType[],
    timeTypesConfig: Dictionary<{ type: TimeType, title: string, className: string }>,
    maxCapacityHours: number,
    summaryData: CellSummaryData,
    prevHours: HoursData | undefined,
    _startEditing: (editTimeType?: TimeType) => void
}

const ViewMode = (props: ViewModeProps) => {
    const { viewType, timeType, timeTypesConfig, maxCapacityHours, summaryData, prevHours, _startEditing } = props;
    const isEmpty = !summaryData.hours.capacity && !summaryData.hours.availability && !summaryData.hours.plan 
        && !summaryData.hours.actual && !summaryData.hours.remaining && !summaryData.hasOverage && !summaryData.hasLocalOverage;
    const isTimeTypeOn = (_: TimeType) => timeType.includes(_);

    if(viewType === ViewType.Hours) {
        return <>
            {isTimeTypeOn('plan') && <CellValue value={formatValue(summaryData.hours.plan)} unit='hours' isEmpty={isEmpty} title={timeTypesConfig.plan.title} className={timeTypesConfig.plan.className}
                onDoubleClick={() => _startEditing('plan')} />}
            {isTimeTypeOn('actual') && <CellValue value={formatValue(summaryData.hours.actual)} unit='hours' isEmpty={isEmpty} title={timeTypesConfig.actual.title} className={timeTypesConfig.actual.className}
                onDoubleClick={() => _startEditing('actual')} />}
            {isTimeTypeOn('remaining') && <CellValue value={formatValue(summaryData.hours.remaining)} unit='hours' isEmpty={isEmpty} title={timeTypesConfig.remaining.title}
                className={timeTypesConfig.remaining.className} />}
            {isTimeTypeOn('availability') && <CellValue value={formatValue(summaryData.hours.availability)} unit='hours' isEmpty={isEmpty} title={timeTypesConfig.availability.title}
                className={timeTypesConfig.availability.className} />}
            {isTimeTypeOn('capacity') && <CellValue value={formatValue(summaryData.hours.capacity)} unit='hours' isEmpty={isEmpty} title={timeTypesConfig.capacity.title} className={timeTypesConfig.capacity.className} />}
        </>;
    }
    if(viewType === ViewType.Percent) {
        return <>
            {isTimeTypeOn('plan') && <CellValue value={summaryData.hours.planPercent !== undefined ? formatValue(summaryData.hours.planPercent) : 'N/A'} unit='%'
                isEmpty={isEmpty} title={timeTypesConfig.plan.title} onDoubleClick={() => _startEditing('plan')} className={timeTypesConfig.plan.className} />}
            {isTimeTypeOn('actual') && <CellValue value={summaryData.hours.actualPercent !== undefined ? formatValue(summaryData.hours.actualPercent) : 'N/A'} unit='%'
                isEmpty={isEmpty} title={timeTypesConfig.actual.title} onDoubleClick={() => _startEditing('actual')} className={timeTypesConfig.actual.className} />}
            {isTimeTypeOn('remaining') && <CellValue value={summaryData.hours.remainingPercent === undefined ? 'N/A' : formatValue(summaryData.hours.remainingPercent)} unit='%'
                isEmpty={isEmpty} title={timeTypesConfig.remaining.title} className={timeTypesConfig.remaining.className} />}
            {isTimeTypeOn('availability') && <CellValue value={summaryData.hours.availability !== undefined ? formatValue(summaryData.hours.availabilityPercent) : 'N/A'} unit='%'
                isEmpty={isEmpty} title={timeTypesConfig.availability.title} className={timeTypesConfig.availability.className} />}
            {isTimeTypeOn('capacity') && <CellValue value={summaryData.hours.capacity ? '100' : 'N/A'} unit='%' isEmpty={isEmpty} title={timeTypesConfig.capacity.title} className={timeTypesConfig.capacity.className} />}
        </>;
    }
    if(viewType === ViewType.FTE) {
        return <>
            {isTimeTypeOn('plan') && <CellValue value={summaryData.hours.planFte !== undefined ? formatValue(summaryData.hours.planFte) : 'N/A'} 
                unit='FTE' isEmpty={isEmpty} title={timeTypesConfig.plan.title} onDoubleClick={() => _startEditing('plan')} className={timeTypesConfig.plan.className} />}
            {isTimeTypeOn('actual') && <CellValue value={summaryData.hours.actualFte !== undefined ? formatValue(summaryData.hours.actualFte) : 'N/A'}
                unit='FTE' isEmpty={isEmpty} title={timeTypesConfig.actual.title} onDoubleClick={() => _startEditing('actual')} className={timeTypesConfig.actual.className} />}
            {isTimeTypeOn('remaining') && <CellValue value={summaryData.hours.remainingFte === undefined ? 'N/A' : formatValue(summaryData.hours.remainingFte)}
                unit='FTE' isEmpty={isEmpty} title={timeTypesConfig.remaining.title} className={timeTypesConfig.remaining.className} />}
            {isTimeTypeOn('availability') && <CellValue value={summaryData.hours.availability !== undefined ? formatValue(summaryData.hours.availabilityFte) : 'N/A'}
                unit='FTE' isEmpty={isEmpty} title={timeTypesConfig.availability.title} className={timeTypesConfig.availability.className} />}
            {isTimeTypeOn('capacity') && <CellValue value={summaryData.hours.capacity ? formatValue(summaryData.hours.capacity / summaryData.fullFteCapacityHours) : 'N/A'}
                unit='FTE' isEmpty={isEmpty} title={timeTypesConfig.capacity.title} className={timeTypesConfig.capacity.className} />}
        </>;
    }
    if(viewType === ViewType.Chart) {
        return <>
            {isTimeTypeOn('plan') && 
                <Chart hours={summaryData.hours.plan} prevHours={prevHours?.plan} maxCapacityHours={maxCapacityHours} 
                    className={timeTypesConfig.plan.className} title={timeTypesConfig.plan.title} onDoubleClick={() => _startEditing('plan')}/> }
            {isTimeTypeOn('actual') && 
                <Chart hours={summaryData.hours.actual} prevHours={prevHours?.actual} maxCapacityHours={maxCapacityHours} 
                    className={timeTypesConfig.actual.className} title={timeTypesConfig.actual.title} onDoubleClick={() => _startEditing('actual')}/>}
            {isTimeTypeOn('remaining') &&
                <Chart hours={summaryData.hours.remaining} prevHours={prevHours?.remaining} maxCapacityHours={maxCapacityHours}
                    className={timeTypesConfig.remaining.className} title={timeTypesConfig.remaining.title}/>}
            {isTimeTypeOn('availability') && 
                <Chart hours={summaryData.hours.availability} prevHours={prevHours?.availability} maxCapacityHours={maxCapacityHours} 
                    className={timeTypesConfig.availability.className} title={timeTypesConfig.availability.title} onDoubleClick={() => _startEditing('availability')}/> }
            {isTimeTypeOn('capacity') && 
                <Chart hours={summaryData.hours.capacity} prevHours={prevHours?.capacity} maxCapacityHours={maxCapacityHours} 
                    className={timeTypesConfig.capacity.className} title={timeTypesConfig.capacity.title} onDoubleClick={() => _startEditing('capacity')}/> } 
        </>
    }
    return null;
}

export default ViewMode;

type ChartProps = {
    hours: number, 
    prevHours?: number, 
    maxCapacityHours: number, 
    className: string, 
    title: string,
    onDoubleClick?: React.MouseEventHandler<HTMLDivElement>
}

const BarMaxHeight = 90;
const BarNormalHeight = 50;
const Chart = (props: ChartProps) => {
    const { hours, prevHours, maxCapacityHours, className, title, onDoubleClick} = props;
    const getBarHeight = (hoursValue: number, capacityValue: number): number => Math.min(BarMaxHeight, BarNormalHeight * hoursValue / capacityValue);

    return <>
        <div onDoubleClick={onDoubleClick} className={`bar ${className}`} title={`${title}: ${formatValue(hours)} hours`} style={{
            bottom: `${getBarHeight(hours, maxCapacityHours)}%`
        }}><div className={`dot ${className}`}></div></div>
        {prevHours !== undefined && prevHours !== hours && <div className={`bar-left-border ${className}`} style={{
            height: `calc(${getBarHeight(Math.max(prevHours, hours), maxCapacityHours) - getBarHeight(Math.min(prevHours, hours), maxCapacityHours)}% + 2px)`,
            bottom: `${getBarHeight(Math.min(prevHours, hours), maxCapacityHours)}%`
        }}></div>}
        {prevHours !== undefined && prevHours !== hours && <div className={`bar corner ${className}`} style={{
            height: `calc(${getBarHeight(prevHours, maxCapacityHours)}% + 2px)`
        }}></div>}
    </>
}
